// @ts-nocheck

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectEntitiesMatchesEntity } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { makeMatchCompetitorsSelector } from '../../selectors/match.selector';
import { MatchEntity, MatchEntityStatus } from '../../states/entities/matches';
import { CategoryIds } from '../../types/categories';
import { SportIds } from '../../types/sports';
import selectors from '../../utils/scoreboards';
import ScoreboardDefaultLayout from '../ScoreboardDefaultLayout';
import ScoreboardRowSetsLayout from '../ScoreboardRowSetsLayout';

const getSportConfigByCategoryId = (categoryId) => {
  switch (categoryId) {
    case CategoryIds.ESPORT_COUNTER_STRIKE:
      return { layout: ScoreboardRowSetsLayout, selector: selectors.esportCounterStrike };
    case CategoryIds.ESPORT_BASKETBALL:
      return { layout: ScoreboardRowSetsLayout, selector: selectors.basketballAndFootball };
    case CategoryIds.ESPORT_ICE_HOCKEY:
      return { layout: ScoreboardRowSetsLayout, selector: selectors.hockey };
    default:
      return { layout: ScoreboardDefaultLayout, selector: selectors.defaultSelector };
  }
};

const getSportConfig = (sportId, categoryId) => {
  switch (sportId) {
    case SportIds.ESPORT_ICE_HOCKEY:
    case SportIds.ICEHOCKEY: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.hockey };
    }
    case SportIds.TENNIS:
    case SportIds.PADEL: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.tennis };
    }
    // The following sports do not have gameScore
    // We use the last index of setScores as gameScore for the display
    case SportIds.BADMINTON:
    case SportIds.DARTS:
    case SportIds.SQUASH:
    case SportIds.TABLE_TENNIS:
    case SportIds.VOLLEYBALL:
    case SportIds.BOWLS:
    case SportIds.BEACH_VOLLEY: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.lastSetAsGameScore };
    }
    case SportIds.ESPORT_COUNTER_STRIKE: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.esportCounterStrike };
    }
    case SportIds.SNOOKER:
    case SportIds.CRICKET: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.snooker };
    }
    case SportIds.AUSSIE_RULES: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.australianFootball };
    }
    case SportIds.ESPORT_BASKETBALL:
    case SportIds.FOOTBALL:
    case SportIds.BASKETBALL: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.basketballAndFootball };
    }
    case SportIds.BASEBALL: {
      return { layout: ScoreboardRowSetsLayout, selector: selectors.baseball };
    }
    case SportIds.ESPORT: {
      return getSportConfigByCategoryId(categoryId);
    }
    default: {
      return null;
    }
  }
};

type ScoreboardRowContainerProps = {
  matchId: number;
};

const ScoreboardRowContainer = ({ matchId }: ScoreboardRowContainerProps) => {
  const matchSelectorProps = useMemo(() => ({ matchId }), [matchId]);
  const match = useSelector((state) => selectEntitiesMatchesEntity(state, matchSelectorProps));
  const competitorsSelector = makeMatchCompetitorsSelector();
  const competitors = useSelector((state) => competitorsSelector(state, matchSelectorProps));
  const { sportId, categoryId } = match ?? {};

  const isMatchValid = useMemo(
    () => typeof sportId === 'number' && typeof categoryId === 'number',
    [categoryId, sportId],
  );
  const config = useMemo(
    () => (isMatchValid ? getSportConfig(sportId, categoryId) : null),
    [isMatchValid, sportId, categoryId],
  );

  const { selector, layout: Layout } = config ?? {};
  const selectorProps = useMemo(
    () => ({
      ...match,
      competitor1ShortName: competitors[0]?.shortname ?? '',
      competitor2ShortName: competitors[1]?.shortname ?? '',
    }),
    [match, competitors],
  );
  const selectedProps = useMemo(() => (selector ? selector(selectorProps, false) : {}), [selectorProps, selector]);

  const enhancedProps = useMemo(
    () => ({
      sportId: match?.sportId,
      categoryId: match?.categoryId,
      isLive: MatchEntityStatus.isLive(match?.status),
      isEnded: MatchEntity.isEnded(match),
      ...selectedProps,
    }),
    [match, selectedProps],
  );

  if (Layout) {
    return <Layout {...enhancedProps} />;
  }

  return null;
};

export default ScoreboardRowContainer;
